import { h } from '@atomify/jsx';
import { FormElement, InputElement, RadioElement } from '@atoms/form-elements';
import { Honeypot } from '@atoms/form-elements/honeypot/honeypot';
import { Col, Row } from '@bpd-library/components';
import { SubscriptionStoreAddress, SubscriptionStoreUser } from '@store/subscription/store.types';
import { useTranslate } from '@utilities/hooks';

export type PreFilledInputs = Partial<SubscriptionStoreUser & SubscriptionStoreAddress>;
export interface AreaSubscriptionFormInterface {
    areaOnly?: boolean;
    formReversed?: boolean;
    classes?: string;
    preFilledInputs?: PreFilledInputs;
}

export const AreaSubscriptionForm = ({
    areaOnly,
    formReversed,
    preFilledInputs,
    classes,
}: AreaSubscriptionFormInterface) => {
    return (
        <FormElement
            hook="area-subscription-modal-form"
            classes={`c-area-subscription-form ${classes}`.trim()}>
            <Row>
                <Col cols={[12, , 11]}>
                    <p class="u-h5 u-h3--md u-margin-top-6">
                        {useTranslate('AreaSubscription.Modal.Step2.Title')}
                    </p>
                </Col>
            </Row>
            <Row>
                <Col cols={[12, , 10]}>
                    <p class="u-margin-top-3 u-margin-bottom-3 s-rich-text">
                        {useTranslate('AreaSubscription.Modal.Step2.Description')}
                    </p>
                </Col>
            </Row>
            <div
                class={`area-subscription-form ${
                    formReversed ? 'area-subscription-form--reversed' : ''
                }`}>
                <div js-hook-area-subscription-contact-details-section>
                    <Row>
                        <Col cols={[12, , 9]}>
                            <p class="u-h6 u-h4--md u-h6 u-h4--md area-subscription-form__title">
                                {useTranslate('AreaSubscription.Modal.Step2.ContactTitle')}
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, , 9]}>
                            <p class="area-subscription-form__description">
                                {useTranslate('AreaSubscription.Modal.Step2.ContactDescription')}
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, , 8]}>
                            <InputElement
                                label={useTranslate('EmailAdres')}
                                id="area-subscription-form-email"
                                name="email"
                                validationTypes="required,email"
                                value={preFilledInputs?.email}
                                readonly={!!preFilledInputs?.email}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, , 8]}>
                            <RadioElement
                                validationTypes="required"
                                horizontal
                                options={[
                                    {
                                        name: 'gender',
                                        id: 'area-subscription-form-gender-dhr',
                                        label: useTranslate('Acronym-Mister'),
                                        value: 'Mr',
                                        checked: preFilledInputs?.gender === 'Mr',
                                    },
                                    {
                                        name: 'gender',
                                        id: 'area-subscription-form-gender-ms',
                                        label: useTranslate('Acronym-Misses'),
                                        value: 'Mrs',
                                        checked: preFilledInputs?.gender === 'Mrs',
                                    },
                                ]}
                                label={useTranslate('Salutation')}
                                disabled={!!preFilledInputs?.gender}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, , 8]}>
                            <InputElement
                                label={useTranslate('FirstName')}
                                id="area-subscription-form-first-name"
                                name="first-name"
                                validationTypes="required,text"
                                value={preFilledInputs?.firstName}
                                readonly={!!preFilledInputs?.firstName}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col cols={[12, , 3]}>
                            <InputElement
                                label={useTranslate('LastNamePrefix')}
                                id="area-subscription-form-last-name-prefix"
                                name="last-name-prefix"
                                validationTypes="text"
                                value={preFilledInputs?.lastNamePrefix}
                                readonly={!!preFilledInputs?.lastNamePrefix}
                            />
                        </Col>
                        <Col cols={[12, , 5]}>
                            <InputElement
                                label={useTranslate('LastName')}
                                id="area-subscription-form-last-name"
                                name="last-name"
                                validationTypes="required,text"
                                value={preFilledInputs?.lastName}
                                readonly={!!preFilledInputs?.lastName}
                            />
                        </Col>
                    </Row>
                </div>
                {!areaOnly && (
                    <div js-hook-area-subscription-address-details-section>
                        <Row>
                            <Col cols={[12, , 9]}>
                                <p class="u-h6 u-h4--md area-subscription-form__title">
                                    {useTranslate('AreaSubscription.Modal.Step2.AddressTitle')}
                                </p>
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 9]}>
                                <p class="area-subscription-form__description">
                                    {useTranslate(
                                        'AreaSubscription.Modal.Step2.AddressDescription',
                                    )}
                                </p>
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 3]}>
                                <InputElement
                                    bpdInputHook="area-subscription-form-zipcode"
                                    label={useTranslate('Postcode')}
                                    id="area-subscription-form-zipcode"
                                    name="zipcode"
                                    validationTypes="required,zipCode"
                                    value={preFilledInputs?.zipcode}
                                    readonly={!!preFilledInputs?.zipcode}
                                />
                            </Col>
                            <Col cols={[8, , 3]}>
                                <InputElement
                                    bpdInputHook="area-subscription-form-housenumber"
                                    label={useTranslate('HouseNumber')}
                                    id="area-subscription-form-house-number"
                                    name="house-number"
                                    validationTypes="required,number"
                                    value={preFilledInputs?.houseNumber}
                                    readonly={!!preFilledInputs?.houseNumber}
                                />
                            </Col>
                            <Col cols={[4, , 2]}>
                                <InputElement
                                    bpdInputHook="area-subscription-form-houseaddition"
                                    label={useTranslate('HouseAddition')}
                                    id="area-subscription-form-house-number-addition"
                                    name="house-number-addition"
                                    validationTypes="text"
                                    value={preFilledInputs?.houseNumberAddition}
                                    readonly={!!preFilledInputs?.houseNumberAddition}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 8]}>
                                <InputElement
                                    bpdInputHook="area-subscription-form-streename"
                                    label={useTranslate('StreetName')}
                                    id="area-subscription-form-street"
                                    name="street"
                                    validationTypes="required,text"
                                    value={preFilledInputs?.street}
                                    readonly={!!preFilledInputs?.street}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 8]}>
                                <InputElement
                                    bpdInputHook="area-subscription-form-city"
                                    label={useTranslate('Place')}
                                    id="area-subscription-form-city"
                                    name="city"
                                    validationTypes="required,text"
                                    value={preFilledInputs?.city}
                                    readonly={!!preFilledInputs?.city}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 8]}>
                                <InputElement
                                    label={useTranslate('PhoneNumber')}
                                    id="area-subscription-form-phone"
                                    name="phone-number"
                                    validationTypes="required,phone"
                                    value={preFilledInputs?.phoneNumber}
                                    readonly={!!preFilledInputs?.phoneNumber}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col cols={[12, , 8]}>
                                <select-country
                                    id="country"
                                    name="country"
                                    label={useTranslate('land')}
                                    preFilledCountry={preFilledInputs?.country}
                                />
                            </Col>
                        </Row>
                    </div>
                )}
            </div>
            <Honeypot id="area-subscription-form-website" name="website" />
        </FormElement>
    );
};
